import { useState } from 'react';
import { brandStory, testimonials, siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q9gfknlsngrm4lltu0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).filter(([k]) => k !== 'phone_alt').map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch (_err) {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于初音岛粉丝资讯站',
    description: brandStory.intro,
  };

  return (
    <main className="min-h-screen pt-16">
      <script type="application/ld+json">{JSON.stringify(aboutLd)}</script>

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
            关于我们
          </h1>
          <p className="text-foreground-500 max-w-2xl text-sm md:text-base">
            {brandStory.intro}
          </p>
        </div>
      </div>

      {/* Brand Story */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              {brandStory.title}
            </h2>
            <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
              {brandStory.mission}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {brandStory.values.map((val) => (
              <div
                key={val.title}
                className="p-6 rounded-lg border border-background-200/70 bg-background-50 text-center"
              >
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                  {val.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {val.description}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {brandStory.achievements.map((ach) => (
              <div
                key={ach.label}
                className="text-center p-6 rounded-lg bg-accent-50 border border-accent-200/70"
              >
                <div className="text-2xl md:text-3xl font-bold text-accent-600 font-heading mb-1">
                  {ach.number}
                </div>
                <div className="text-xs md:text-sm text-foreground-500">{ach.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              用户口碑
            </h2>
            <p className="text-foreground-500 text-sm">听听社区成员们怎么说</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="p-6 rounded-lg border border-background-200/70 bg-background-50"
              >
                <div className="flex items-center gap-2 mb-3 text-accent-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "{t.content}"
                </p>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${t.avatar} text-lg`}></i>
                  </span>
                  <div>
                    <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm">
              有任何问题或建议？请通过以下表单与我们取得联系
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="space-y-5"
          >
            <div className="field-website-alt-wrapper">
              <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                主题 <span className="text-primary-500">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                required
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入消息主题"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                消息内容 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                placeholder="请输入您的消息内容，最多500字"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
            </div>

            {formStatus === 'success' && (
              <div className="p-4 rounded-lg bg-secondary-50 border border-secondary-200/70 text-sm text-secondary-800 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-lg"></i>
                消息已成功发送！我们会尽快回复您。
              </div>
            )}

            {formStatus === 'error' && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200/70 text-sm text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line text-lg"></i>
                {formError}
              </div>
            )}

            <button
              type="submit"
              className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              发送消息 <i className="ri-send-plane-line ml-1"></i>
            </button>
          </form>

          <div className="mt-12 p-6 rounded-lg bg-background-100 border border-background-200/70">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-3">联系信息</h4>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 text-sm text-foreground-600">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-mail-line"></i>
                    </span>
                    {siteInfo.email}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground-600">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    {siteInfo.address}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-3">社交媒体</h4>
                <div className="flex gap-3">
                  <a
                    href={siteInfo.socialLinks.weibo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 border border-background-200/70 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-all"
                  >
                    <i className="ri-weibo-line text-lg"></i>
                  </a>
                  <a
                    href={siteInfo.socialLinks.bilibili}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 border border-background-200/70 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-all"
                  >
                    <i className="ri-bilibili-line text-lg"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}